import React from 'react';
import { InfoTip } from '@/pages/reports/_Components/InfoTip';
import { TONES, type Tone } from '../theme';

interface Props {
  label: string;
  value: string;
  sub?: string;
  info?: string;
  tone?: Tone;
  icon?: React.ReactNode;
  onClick?: () => void;
  /** lg: headline card with a soft colour wash; md: default; sm: compact, for "More insights". */
  size?: 'lg' | 'md' | 'sm';
  /** Drops the faint corner icon. Default keeps the original look for the other reports. */
  hideWatermark?: boolean;
}

/**
 * White card, thin border, a pastel icon tile. No resting shadow: a clickable card lifts on
 * hover, which is also the only hint that it opens a drilldown.
 */
const StatCard: React.FC<Props> = ({ label, value, sub, info, tone = 'slate', icon, onClick, size = 'md', hideWatermark = false }) => {
  const t = TONES[tone];
  const pad = size === 'lg' ? 'px-5 py-4' : size === 'sm' ? 'px-3.5 py-3' : 'px-4 py-3.5';
  const bg = `bg-gradient-to-br ${t.grad}`;
  const tile = size === 'lg' ? 'h-10 w-10 rounded-xl' : size === 'sm' ? 'h-7 w-7 rounded-lg' : 'h-8 w-8 rounded-lg';
  const num = size === 'lg' ? 'text-3xl' : size === 'sm' ? 'text-xl' : 'text-2xl';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`relative overflow-hidden text-left rounded-2xl border border-slate-200 transition-all dark:border-slate-700 ${pad} ${bg} ${onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md' : 'cursor-default'}`}
    >
      {icon && !hideWatermark && React.isValidElement(icon) && (
        <span aria-hidden className={`pointer-events-none absolute -bottom-3 -right-2 opacity-25 ${t.watermark}`}>
          {React.cloneElement(icon as React.ReactElement<{ className?: string }>, { className: size === 'sm' ? 'h-14 w-14' : 'h-20 w-20' })}
        </span>
      )}
      <div className="relative flex items-start gap-3">
        {icon && <span className={`flex shrink-0 items-center justify-center ${tile} ${t.icon}`}>{icon}</span>}
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-xs font-medium text-slate-700 dark:text-slate-200">
            {label}{info && <InfoTip text={info} />}
          </p>
          <p className={`mt-1 font-bold tracking-tight text-slate-800 dark:text-white ${num}`}>{value}</p>
          {sub && <p className="mt-0.5 text-xs text-slate-700 dark:text-slate-300">{sub}</p>}
        </div>
      </div>
    </button>
  );
};

export default StatCard;
